import type {
  CanonicalReportQuoteEvidence,
  CanonicalReportProduct,
  SeoGeneratedComponents,
  SeoSocialSignal,
  SeoSocialSignalPlatform,
} from '@dupe/common/research-report'

function mapQuoteEvidenceForDisplay(quote: CanonicalReportQuoteEvidence) {
  // Legacy evidence carries `quote.platform` as a free-text string, not the
  // SeoSocialSignalPlatform enum — skip the platform field on this path so
  // QuoteCard falls back to its detectPlatform shim.
  return {
    text: quote.text,
    source: quote.sourceLabel,
    url: quote.sourceUrl ?? null,
  }
}

function mapSocialSignalForDisplay(signal: SeoSocialSignal) {
  return {
    text: signal.quote,
    source: signal.sourceLabel,
    url: signal.sourceUrl,
    platform: signal.platform,
  }
}

interface GeneratedQuoteState {
  isReady: boolean
  quotesBySlug: Map<string, CanonicalReportQuoteEvidence[]>
}

interface GeneratedSocialSignalsState {
  isReady: boolean
  signalsBySlug: Map<string, SeoSocialSignal[]>
}

export function buildGeneratedQuoteState(
  generatedComponents: SeoGeneratedComponents | null | undefined
): GeneratedQuoteState {
  const productQuotes = generatedComponents?.product_quotes
  if (productQuotes?.status !== 'ready') {
    return { isReady: false, quotesBySlug: new Map() }
  }

  return {
    isReady: true,
    quotesBySlug: new Map(
      (productQuotes.content?.products ?? []).map((entry) => [
        entry.slug,
        entry.quotes,
      ])
    ),
  }
}

export function buildGeneratedSocialSignalsState(
  generatedComponents: SeoGeneratedComponents | null | undefined
): GeneratedSocialSignalsState {
  const socialSignals = generatedComponents?.social_signals
  if (socialSignals?.status !== 'ready') {
    return { isReady: false, signalsBySlug: new Map() }
  }

  return {
    isReady: true,
    signalsBySlug: new Map(
      (socialSignals.content?.products ?? []).map((entry) => [
        entry.slug,
        entry.signals,
      ])
    ),
  }
}

interface CommunityQuoteCardData {
  text: string
  source?: string
  url: string | null
  platform?: SeoSocialSignalPlatform
}

export function buildCommunityQuotes(
  product: CanonicalReportProduct,
  generatedSocialSignalsState: GeneratedSocialSignalsState,
  generatedQuoteState: GeneratedQuoteState,
  subcategorySlug: string | null | undefined
): CommunityQuoteCardData[] {
  // Fragrance reports render no quote cards: the legacy quote fields do not
  // distinguish verbatim evidence from synthesis. `subcategorySlug` is the
  // report's own subcategory (resolveReportSubcategorySlug), not the
  // override-adjusted `seo.meta.subcategorySlug`.
  if (subcategorySlug === 'fragrance') return []

  // Priority 1: structured social_signals from the LLM. Carries an explicit
  // `platform` field so the carousel doesn't need keyword-matching on the
  // source label or URL host.
  const generatedSignals = generatedSocialSignalsState.isReady
    ? generatedSocialSignalsState.signalsBySlug.get(product.slug)
    : undefined
  if (generatedSignals && generatedSignals.length > 0) {
    return generatedSignals.slice(0, 3).map(mapSocialSignalForDisplay)
  }

  // Priority 2: product_quotes structured generation. Same plain text + label
  // shape; carousel keyword-detects the platform from those fields.
  // The pipeline writes a `{ slug, quotes: [] }` placeholder for every
  // product, even ones with no direct evidence, so `has(slug)` would make
  // us drop the socialEvidence/whyCommunity fallback below.
  const generatedQuotes = generatedQuoteState.isReady
    ? generatedQuoteState.quotesBySlug.get(product.slug)
    : undefined
  if (generatedQuotes && generatedQuotes.length > 0) {
    return generatedQuotes.slice(0, 3).map(mapQuoteEvidenceForDisplay)
  }

  if ((product.socialEvidence?.quotes ?? []).length > 0) {
    return product.socialEvidence.quotes
      .slice(0, 3)
      .map(mapQuoteEvidenceForDisplay)
  }

  return (product.ranking?.whyCommunity ?? []).slice(0, 3).map((text) => ({
    text,
    url: null,
  }))
}
